/**
 * The unified production-record input — ONE screen for a service day, one
 * WIZARD STEP per mealtime (PROD-1279 stage-1 feedback, Track B).
 *
 * The shipped surface splits a day into two tasks: Transport logs (what the
 * truck brought, at what temperature) and Service records (what went out, to
 * whom, at what temperature). The PRINTED record is one table per mealtime, and
 * an admin filling records at week's end is copying that sheet. This screen is
 * that sheet: a left rail of MEALTIMES (the chef's step rail), and per step the
 * mealtime as the title, the scan card, one bare table whose rows are the
 * record's own (one per item × age band, every numeric cell an input), the
 * `Write-ins & unservable meals` card, the Kitchen / Delivery / Service cards,
 * and the buttons at the end of the content. No auto-advance: the user picks
 * the next step; the dialog closes only when the last unsigned record is signed.
 *
 * WHAT IT SHARES with the two shipped flows, deliberately and not by copy:
 *   - the production draft (per service day), so work started in one mode is
 *     there in the other; the transport temperatures ride in the same `temps`
 *     map under `facility::` / `delivery::` keys the shipped task never reads;
 *   - `seedProductionEdits` / `deriveProductionCounts` / `distributeProductionFill`
 *     / `serviceTempSaveEntries` / `writeInSaveEntries`, so the two count
 *     identities and the save payloads are one implementation;
 *   - `WriteInsFooter` itself, imported from the service flow;
 *   - `distributeReceivedTotal`, so an item's Received total still fans back
 *     out to its shipment lines.
 *
 * PERSISTENCE (B5): the four service counts AND the school's Ordered / Prepared
 * / Received go to the record's own roster buckets (`/quantities`); Received
 * ALSO still receives the trucks (`receive-delivery` per pending or touched
 * shipment, the item total fanned to its lines) so tasks close and the day
 * rollup stays right; the temperatures of an item WITH truck lines ride on the
 * receipt, those of an item WITHOUT go to `/service-temps` with a `checkpoint`
 * as RECORD-scoped logs. Never a `ShipmentEvent` for a record-scoped value.
 *
 * WEEKLY MILK (Abner, Sep 18): the record's milk rows are fed by the campus's
 * weekly drop (`RecordRow.milk`). One SKU feeds several rows across bands AND
 * steps, so its three transport cells are ONE SCOPE: every row shows the same
 * value, an edit on any row is written through to every step that has a row of
 * the scope, and the drop's Kitchen °F / Received °F are one scope per drop the
 * same way. Their Received persists through the RECEIPT (the scope total fanned
 * across the drop's shipment lines, once per shipment across steps); a scope
 * value is the TRUCK's, never stamped on a band's bucket — the document sums
 * ENTERED buckets per line, and two bands each stamped 46 would print 92.
 *
 * ONE PRIMARY ACTION (Abner, Sep 18): `Save for later` and `Sign & complete`,
 * nothing else. `Sign & complete` receives the step's trucks first (pending or
 * touched, once per shipment across steps), then runs the record chain; on a
 * record-less step it does the receipts alone and marks the step done. There
 * is no `Confirm deliveries` button.
 *
 * NOT A NATIVE `<form>` (PROD-1279 ruling E1, overriding `apps/ordo/CLAUDE.md`
 * § Forms for this file). Enter in a count box would confirm a delivery or
 * sign a USDA record the moment the gates pass, and both name fields are
 * prefilled. Every button is an explicit `type="button"`.
 */

import { Button } from '@ordo/ui/button'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { Info } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import {
  cafeteriaDeliveriesKey,
  type CafeteriaDeliveryWire,
  fetchCafeteriaDeliveries,
  type Mealtime,
  useReceiveDeliveryMutation,
} from '@/api/cafeteria'
import {
  type ProductionFillMealtime,
  type ProductionFillResponse,
  type SaveQuantitiesInput,
  type SaveServiceTempsInput,
  useProductionFillQuery,
  useRecentServiceMenuItemsQuery,
  useSaveProductionQuantitiesMutation,
  useSaveProductionWriteInsMutation,
  useSaveServiceTemperaturesMutation,
  useSignProductionRecordMutation,
} from '@/api/cafeteria-production'
import { ApiError } from '@/api/http'
import { fetchProductionRecordDocument, type ProductionRecordDocument } from '@/api/production-records'
import { type ScanSheetResponse, useScanSheetMutation } from '@/api/sheet-scan'
import { CafeteriaModeTip } from '@/components/cafeteria/cafeteria-mode-tip'
import { dayItemOptions, WriteInsFooter } from '@/components/cafeteria/complete-service-flow'
import { CafeteriaWorkflowHeader } from '@/components/cafeteria/workflow-header'
import { MobileRecordWizard } from '@/components/production/mobile/mobile-record-wizard'
import { sectionCaption, serviceStyleDisplay } from '@/components/production/record-insights'
import { SheetUploadStrip } from '@/components/production/sheets/sheet-upload-strip'
import { MealtimeStepList } from '@/components/production/spreadsheet/mealtime-step-list'
import {
  type RecordCell,
  type RecordColumnKey,
  type RecordSpreadsheetGroupView,
  type RecordSpreadsheetRowView,
  RecordSpreadsheetTable,
} from '@/components/production/spreadsheet/record-spreadsheet-table'
import { DeliveryCard, KitchenCard, ServiceCard } from '@/components/production/spreadsheet/record-step-cards'
import { type ScanApplySummary, SCHOOL_SHEET_SCAN_COPY } from '@/components/tasks/sheet-scan-card'
import { usePhoneViewport } from '@/hooks/use-phone-viewport'
import {
  aggregateDeliveryRows,
  distributeReceivedTotal,
  splitMilkDeliveries,
  tempCheckRequired,
} from '@/lib/cafeteria/aggregate-deliveries'
import {
  distributeProductionFill,
  formatServiceDateLabel,
  MEALTIME_ORDER,
  type ProductionEdits,
  productionFillRowKey,
  type ProductionRowEdit,
  type ProductionWriteInEdit,
  seedProductionEdits,
  serviceTempSaveEntries,
  signatoryNameFor,
  writeInRowIncomplete,
  writeInSaveEntries,
} from '@/lib/cafeteria/format'
import {
  blocksSubmit,
  type CountField,
  type CountViolation,
  countViolationMessage,
  deriveProductionCounts,
  type ProductionCounts,
  seedProductionCounts,
} from '@/lib/cafeteria/production-counts'
import {
  combineReadingInstant,
  defaultReadingTime,
  isReadingTime,
  readingTimeFromInstant,
} from '@/lib/cafeteria/reading-time'
import { outsideHoldingBand } from '@/lib/cafeteria/temperature-edit'
import { taskDateFirstLabel } from '@/lib/production-records/day-tasks'
import {
  applyDeliveryScan,
  buildDeliveryScanRows,
  deliveryDatesLabel,
  deliveryScanFieldTotal,
  receivedByFromScan,
} from '@/lib/production-records/delivery-scan'
import {
  claimedMeals,
  mainsServedTogether,
  type MealClaimRow,
  SERVED_TOGETHER_CAPTION,
  SERVED_TOGETHER_TITLE,
} from '@/lib/production-records/meal-claim'
import {
  combineScanParts,
  countPhotoWarnings,
  deliveryStripLine,
  recordStripLine,
  type ScanSheetPart,
  SHEET_PAPER_NAME,
  type SheetPhotos,
  stepDeliveryDates,
} from '@/lib/production-records/sheet-papers'
import {
  applyRecordScan,
  buildRecordScanRows,
  buildRecordSteps,
  cellIdFor,
  cellOffered,
  countCellId,
  countCellIdFor,
  documentMealPattern,
  groupRecordRows,
  isMilkStartRow,
  isPreparedRow,
  isSignedStatus,
  isTempColumn,
  isTransportCountColumn,
  KIT_COUNTS_NOTE,
  kitBadge,
  kitDisplayTitle,
  milkStartCell,
  milkStartDefault,
  partitionMilkOnlyDeliveries,
  prefillIsAssumption,
  RECORD_COLUMNS,
  type RecordColumn,
  type RecordRow,
  recordRowMealKeys,
  recordScanFieldTotal,
  type RecordScanTarget,
  type RecordStep,
  rederiveCountsForPrepared,
  SERVED_OVER_RECEIVED_NOTE,
  servedOverReceivedHeadline,
  SERVICE_COUNT_COLUMNS,
  type ServiceCountColumn,
  type SpreadsheetMilkGroup,
  type SpreadsheetTransportGroup,
  stepProgress,
  tempCellIdFor,
  type TempColumn,
  type TempScanPatch,
  tempScopeKeyFor,
  tempStoreKeyFor,
  type TransportCountColumn,
  transportStoreKeyFor,
  truckReceiptFor,
  type TruckSummary,
} from '@/lib/production-records/spreadsheet-model'
import {
  buildStepBlockers,
  OTHER_BLOCKER,
  type StepBlocker,
  type StepBlockerRow,
} from '@/lib/production-records/step-blockers'
import {
  isScanAbort,
  parseTimeReading,
  remapPhotoIndices,
  SCAN_TIMEOUT_MESSAGE,
  type ScanPartialSummary,
} from '@/lib/sheet-scan/apply-result'
import type { ScanRowWire } from '@/lib/sheet-scan/build-request'
import { filesToImagePayloads } from '@/lib/sheet-scan/images'
import {
  overlayProductionEdits,
  productionDraftKey,
  useCafeteriaProductionDraftStore,
} from '@/stores/cafeteria-production-draft'
import { receiveDraftKey, useCafeteriaReceiveDraftStore } from '@/stores/cafeteria-receive-draft'
import { useProfile } from '@/stores/profile'

/** The four interlocked counts, in the order derivation must see them. */
const COUNT_FIELDS = SERVICE_COUNT_COLUMNS

/** The accessible-name suffix per column: `Chicken bowl K-5 served`. */
const INPUT_LABEL: Record<RecordColumn, string> = {
  ordered: 'planned',
  // The kitchen's own prepared count: kept on the row model, no longer shown.
  prepared: 'kitchen prepared',
  received: 'prepared',
  served: 'served',
  reimbursable: 'reimbursable',
  nonReimbursable: 'non-reimbursable',
  leftover: 'leftovers',
  kitchenTemp: 'kitchen temperature',
  receivedTemp: 'drop-off temperature',
  servingTemp: 'service temperature',
}

/** The `title` on a box that has nowhere to save (plan-05 ASSUMED-7). */
export const NOT_ON_RECORD_TITLE = 'This item is not on the production record.'

/** Under a phone Served tile the server defaulted to every meal ordered (PROD-1511). */
export const FULL_ORDER_CAPTION = 'Full order'

/** Under the table of a record step that carries a weekly milk SKU no record row claimed. */
/** What the phone's Served-above-Received note says after the two numbers. */
export const BACKSTOCK_NOTE = 'Prepared counts everything on hand, backstock included: fix Served or Prepared.'
export const UNJOINED_MILK_NOTE =
  'Milk rows marked "not on this record" have no service counts here; add served milk as a write-in.'

function isServiceCountColumn(column: RecordColumn): column is ServiceCountColumn {
  return (SERVICE_COUNT_COLUMNS as readonly string[]).includes(column)
}

const ZERO_COUNTS: ProductionCounts = { served: 0, reimbursable: 0, nonReimbursable: 0, leftover: 0 }

export interface RecordSpreadsheetFlowProps {
  locationId: string
  /** yyyy-mm-dd SERVICE day — the production records' axis. */
  serviceDate: string
  /** "{campus} · {day}" — the fullscreen host hides the page, so the screen names itself. */
  subtitle?: string
  /** Every DELIVERY date feeding this service day: the unreceived ones plus any already received. */
  deliveryDates: readonly string[]
  /** `?mealtime=` — the step the URL names. Null: the first unsigned step. */
  mealtime?: Mealtime | null
  /** The user picked a step; the page REPLACES `mealtime` in the URL. */
  onSelectMealtime?: (mealtime: Mealtime) => void
  /** "Save for later" — leave without submitting (the draft persists). */
  onExit: () => void
  /** The last unsigned record was signed. */
  onComplete: () => void
  /**
   * Amend a SIGNED record: the flow shows that record's step alone, open for
   * editing with the same inputs the record was filled with, asks why it is
   * changing, and re-signs it. Every write carries the reason onto the
   * record's amendment trail. Null: the normal fill.
   */
  amend?: { productionRecordId: string } | null
}

/** PROD-1562: the lede of a signed record whose truck was never received. */
const RECEIPT_ONLY_DESCRIPTION =
  'This record is signed, but a delivery for it was never confirmed. Enter what arrived and the time it arrived.'

/** The amend step's lede — the record is signed; this is a correction, on the record. */
export const AMEND_DESCRIPTION =
  'This record is already signed. Correct the counts below; every change is recorded on the amendment trail and the record is re-signed.'

export function RecordSpreadsheetFlow({
  locationId,
  serviceDate,
  subtitle,
  deliveryDates,
  mealtime: mealtimeParam = null,
  onSelectMealtime,
  onExit,
  onComplete,
  amend = null,
}: RecordSpreadsheetFlowProps) {
  const { profile } = useProfile()
  const queryClient = useQueryClient()
  // A phone gets the photo-first wizard (plan-06 lane C); desktop and tablet keep this screen as it is.
  const phone = usePhoneViewport()

  // -- Reads ------------------------------------------------------------------
  const fillQuery = useProductionFillQuery({ schoolLocationId: locationId, serviceDate })
  const fill = fillQuery.data
  const mealtimes = useMemo(() => fill?.mealtimes ?? [], [fill])

  // One document per record: the plan facts (Ordered, Prepared, per-band
  // lines), the chef's readings and the kitchen signature live nowhere on the
  // fill wire. Built with `useQueries` because the count is only known once
  // the fill lands; the key and the fetcher are `useProductionRecordDocumentQuery`'s own.
  const documentQueries = useQueries({
    queries: mealtimes.map((mt) => ({
      queryKey: ['production-records', 'document', mt.productionRecordId] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchProductionRecordDocument(mt.productionRecordId, signal),
      staleTime: 30 * 1000,
    })),
  })
  // Built per render rather than memoized: the `useQueries` result array is a
  // new object every render, so any dependency list over it either never
  // invalidates (stale documents) or always does (a memo that memoizes nothing).
  const documents = new Map<string, ProductionRecordDocument>()
  mealtimes.forEach((mt, i) => {
    const doc = documentQueries[i]?.data
    if (doc) {
      documents.set(mt.productionRecordId, doc)
    }
  })

  const deliveryQueries = useQueries({
    queries: deliveryDates.map((deliveryDate) => ({
      queryKey: cafeteriaDeliveriesKey({ schoolLocationId: locationId, deliveryDate }),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchCafeteriaDeliveries({ schoolLocationId: locationId, deliveryDate }, signal),
      staleTime: 10 * 1000,
    })),
  })
  /**
   * The day's trucks, per DELIVERY date, narrowed to THIS SERVICE DAY.
   * `/cafeteria/deliveries` is keyed by the delivery date alone, so a query for
   * a day-before drop also returns that day's OWN same-day trucks; another
   * day's truck belongs to another day's record.
   */
  const deliveriesByDate: Array<{ deliveryDate: string; deliveries: CafeteriaDeliveryWire[] }> = deliveryDates.map(
    (deliveryDate, i) => ({
      deliveryDate,
      deliveries: (deliveryQueries[i]?.data?.deliveries ?? []).filter((d) => d.serviceDate === serviceDate),
    }),
  )

  /**
   * A read the steps are built from is still on its FIRST fetch. The fill
   * alone is not a screen: a mealtime whose record does not exist yet (the
   * receipt creates it) is a step only once the deliveries land, and a
   * record's Ordered / Prepared and legends arrive with its document. A query
   * WITH data (a background refetch) never counts; one that errored
   * (`isPending` false, no data) is rendered around, not waited on.
   */
  const readsPending =
    (fillQuery.isPending && !fillQuery.data) ||
    documentQueries.some((q) => q.isPending && !q.data) ||
    deliveryQueries.some((q) => q.isPending && !q.data)
  /**
   * Every read landed once: the skeleton is for the FIRST load only. A receipt
   * upserts the mealtime's record on the server, and the next fill refetch
   * (signing invalidates it) mounts a NEW document query for it. Blanking a
   * screen the school is working in, over a document that only sharpens one
   * step, is worse than that step sharpening in place.
   */
  const [readsSettled, setReadsSettled] = useState(false)
  useEffect(() => {
    if (!readsPending) {
      setReadsSettled(true)
    }
  }, [readsPending])

  // -- Edit state -------------------------------------------------------------
  // ONE map for everything the school types, keyed by STEP: a record step's
  // key IS its record id, so the shipped Service records task reads the same
  // entries; a record-less step's key is synthetic and never persisted.
  const draftKey = productionDraftKey(locationId, serviceDate)
  const [edits, setEdits] = useState<ProductionEdits>({})
  const [signatoryName, setSignatoryName] = useState('')
  const signatureTouchedRef = useRef(false)
  const dirtyRef = useRef(false)
  const manualFieldsRef = useRef<Set<string>>(new Set())
  const [rowViolations, setRowViolations] = useState<Record<string, CountViolation[]>>({})
  const [submitting, setSubmitting] = useState(false)
  /** Cells the school (or a scan) edited — an already-received truck is re-sent only when one of its cells is here (D-B3.5). */
  const [touched, setTouched] = useState<Set<string>>(new Set())
  /**
   * Shipments a receipt landed for on this screen, before the deliveries
   * refetch says so. A weekly milk shipment is visible from EVERY step its SKUs
   * join, so without this a second step signed before the refetch would post
   * the same receipt again.
   */
  const [receivedLocally, setReceivedLocally] = useState<Set<string>>(new Set())
  /** Cell ids a photo filled at LOW confidence. */
  const [scanFlags, setScanFlags] = useState<Set<string>>(new Set())
  /** Temp SCOPE key → PHOTO provenance, consumed by the receipt wire. */
  const [scanProvenance, setScanProvenance] = useState<Record<string, { source: 'PHOTO'; confidence: 'high' | 'low' }>>(
    {},
  )
  /** The step the user picked (or just signed) — the rail never moves on its own. */
  const [pickedKey, setPickedKey] = useState<string | null>(null)
  /** The Service card's time per step, once the school touched it. */
  const [serviceTimeOverride, setServiceTimeOverride] = useState<Record<string, string>>({})
  /** Records signed on this screen, before the refetch says so. */
  const [signedLocally, setSignedLocally] = useState<Record<string, { name: string; signedAt: string }>>({})
  /** Why a signed record is being changed (amend mode); required before it re-signs. */
  const [amendReason, setAmendReason] = useState('')

  useEffect(() => {
    if (!signatureTouchedRef.current) {
      const prefill = signatoryNameFor(profile?.user)
      if (prefill !== '') {
        setSignatoryName((current) => (current === '' ? prefill : current))
      }
    }
  }, [profile])

  const seededFillRef = useRef<ProductionFillResponse | null>(null)
  useEffect(() => {
    if (fill && seededFillRef.current !== fill) {
      seededFillRef.current = fill
      const base = seedProductionEdits(fill)
      const draft = useCafeteriaProductionDraftStore.getState().getDraft(draftKey)
      dirtyRef.current = Object.keys(draft).length > 0
      // Record-less steps' entries live only in memory: keep whatever this
      // session already typed for them across a re-seed.
      setEdits((prev) => {
        const merged = overlayProductionEdits(base, draft)
        for (const [key, value] of Object.entries(prev)) {
          if (!(key in base)) {
            merged[key] = value
          }
        }
        return merged
      })
    }
  }, [draftKey, fill])

  // Persist the RECORDS' edits (never a synthetic step, never a signed record).
  useEffect(() => {
    if (!dirtyRef.current) {
      return
    }
    const persisted: ProductionEdits = {}
    for (const mt of mealtimes) {
      const pr = edits[mt.productionRecordId]
      if (pr && !isSignedStatus(mt.status) && !signedLocally[mt.productionRecordId]) {
        persisted[mt.productionRecordId] = pr
      }
    }
    if (Object.keys(persisted).length > 0) {
      useCafeteriaProductionDraftStore.getState().saveDraft(draftKey, persisted)
    }
  }, [draftKey, edits, mealtimes, signedLocally])

  // -- Mutations --------------------------------------------------------------
  const receiveMutation = useReceiveDeliveryMutation()
  const scanMutation = useScanSheetMutation()
  const saveQuantities = useSaveProductionQuantitiesMutation()
  const saveWriteIns = useSaveProductionWriteInsMutation()
  const saveTemps = useSaveServiceTemperaturesMutation()
  const sign = useSignProductionRecordMutation()
  const recentItemsQuery = useRecentServiceMenuItemsQuery({ schoolLocationId: locationId, serviceDate })
  const recentItems = recentItemsQuery.data?.items ?? []

  // -- The join ---------------------------------------------------------------
  const transportGroups: SpreadsheetTransportGroup[] = []
  const milkGroups: SpreadsheetMilkGroup[] = []
  const shipments: TruckSummary[] = []
  for (const entry of deliveriesByDate) {
    for (const d of entry.deliveries) {
      shipments.push({
        shipmentId: d.shipmentId,
        deliveryDate: d.deliveryDate,
        status: d.status,
        receivedAt: d.receivedAt,
        receiverName: d.receiverName,
      })
    }
    // A shipment's lines stay on ONE step: only a milk-ONLY truck is the weekly
    // drop (first step); a meal truck that also carries a milk line keeps it as
    // a chilled row of its own mealtime, so the per-shipment receipt posted from
    // that step carries a temperature for every line it lists.
    const { milkOnly, meal: mealDeliveries } = partitionMilkOnlyDeliveries(entry.deliveries)
    const { milkGroups: milk } = splitMilkDeliveries(milkOnly)
    for (const mealtime of MEALTIME_ORDER) {
      const forMealtime = mealDeliveries.filter((d) => d.mealtime === mealtime)
      if (forMealtime.length === 0) {
        continue
      }
      transportGroups.push({
        deliveryDate: entry.deliveryDate,
        pending: forMealtime.some((d) => d.status === 'NEEDS_RECEIPT'),
        receivedAt: forMealtime.find((d) => d.receivedAt)?.receivedAt ?? null,
        receiverName: forMealtime.find((d) => d.receiverName)?.receiverName ?? null,
        mealtime,
        rows: aggregateDeliveryRows(forMealtime),
      })
    }
    for (const group of milk) {
      milkGroups.push({
        deliveryDate: entry.deliveryDate,
        pending: milkOnly.some((d) => d.status === 'NEEDS_RECEIPT'),
        receivedAt: milkOnly.find((d) => d.receivedAt)?.receivedAt ?? null,
        receiverName: milkOnly.find((d) => d.receiverName)?.receiverName ?? null,
        group,
      })
    }
  }
  const daySteps = buildRecordSteps({ mealtimes, documents, transport: transportGroups, milk: milkGroups, shipments })
  // Amending is scoped to ONE record: its step alone, no rail of siblings.
  const steps = amend ? daySteps.filter((step) => step.productionRecordId === amend.productionRecordId) : daySteps
  const stepsByKey = new Map(steps.map((step) => [step.key, step] as const))

  // -- Which step is done, which is open --------------------------------------
  function truckReceived(truck: TruckSummary): boolean {
    return truck.status === 'RECEIVED' || receivedLocally.has(truck.shipmentId)
  }

  /** The step's record carries a school signature (the one being amended only once re-signed here). */
  function recordSigned(step: RecordStep): boolean {
    if (!step.productionRecordId) {
      return false
    }
    // The record being amended is open again until it is re-signed here.
    if (amend?.productionRecordId === step.productionRecordId) {
      return step.productionRecordId in signedLocally
    }
    return step.signed || step.productionRecordId in signedLocally
  }

  /** The step's shipments still waiting for a receipt. */
  function pendingShipmentIds(step: RecordStep): Set<string> {
    return new Set(step.trucks.filter((truck) => !truckReceived(truck)).map((truck) => truck.shipmentId))
  }

  /**
   * PROD-1562: a SIGNED record over a truck nobody received is not done. Its
   * cells stay locked, but the receipt stays reachable, or the day's percent
   * and the truck's receive task can never close.
   */
  function receiptOnly(step: RecordStep): boolean {
    return recordSigned(step) && pendingShipmentIds(step).size > 0
  }

  function stepDone(step: RecordStep): boolean {
    if (step.productionRecordId) {
      return recordSigned(step) && !receiptOnly(step)
    }
    return step.trucks.length > 0 && step.trucks.every(truckReceived)
  }

  // The URL names a mealtime; the user's own pick wins while it still agrees
  // with the URL (two records of one mealtime share the param). Never the
  // "first unsigned" once anything was picked: no auto-advance.
  const picked = pickedKey ? stepsByKey.get(pickedKey) : undefined
  const activeStep: RecordStep | null =
    (picked && (mealtimeParam === null || picked.mealtime === mealtimeParam) ? picked : undefined) ??
    (mealtimeParam ? steps.find((step) => step.mealtime === mealtimeParam) : undefined) ??
    steps.find((step) => !stepDone(step)) ??
    steps[0] ??
    null

  function selectStep(key: string): void {
    setPickedKey(key)
    const step = stepsByKey.get(key)
    if (step?.mealtime) {
      onSelectMealtime?.(step.mealtime)
    }
  }

  // -- Values -----------------------------------------------------------------
  /**
   * What a cell SHOWS: the school's entry when there is one (a cleared box is
   * `null`), else the row's prefill. The rail, the payloads and the receipt plan
   * all read this one function, so the number on screen is the number that
   * ships.
   */
  function cellValue(step: RecordStep, row: RecordRow, column: RecordColumn): number | null {
    // `undefined` = untouched (show the prefill); `null` = the school CLEARED
    // the box, which must stay empty — hence no `??` over the store reads.
    const pr = edits[step.key]
    if (isTempColumn(column)) {
      const own = pr?.temps[tempStoreKeyFor(row, column)]
      const stored = own !== undefined ? own : scopedTemp(row, column)
      return stored !== undefined ? stored : row.prefill[column].value
    }
    if (column === 'received' && isMilkStartRow(row)) {
      return milkStartFor(step, row).value
    }
    if (column === 'ordered' && isMilkStartRow(row)) {
      // The meal's own Ordered: typed here, else the band's prefill — never another meal's.
      const own = pr?.rows[row.key]?.ordered
      return own !== undefined ? own : row.prefill.ordered.value
    }
    if (isTransportCountColumn(column)) {
      const own = pr?.rows[transportStoreKeyFor(row)]?.[column]
      const stored = own !== undefined ? own : scopedCount(row, column)
      return stored !== undefined ? stored : row.prefill[column].value
    }
    if (!cellOffered(row, column)) {
      return null
    }
    const edit = pr?.rows[row.key]
    return edit ? edit[column] : row.prefill[column].value
  }

  /**
   * A milk row's Prepared (`isMilkStartRow`): the meal's own number (typed, or
   * saved earlier), else the earlier meal's leftover of the same milk today,
   * else this week's carry, else the delivery's count. A CLEARED box falls back
   * to that default: the carry is a suggestion, never a lock.
   */
  function milkStartFor(step: RecordStep, row: RecordRow): ReturnType<typeof milkStartCell> {
    const own = edits[step.key]?.rows[row.key]?.received
    if (own != null) {
      const cell = milkStartCell(own, { value: null, source: 'delivery', label: null, fromTitle: null })
      // Typed on this screen: the school's own number, with no "saved" tip until it saves.
      return touched.has(countCellId(step.key, row.key, 'received')) ? { value: cell.value, tip: null } : cell
    }
    return milkStartCell(
      undefined,
      // The whole day, so an amended lunch still opens on breakfast's leftover.
      milkStartDefault(daySteps, step.key, row, (other, otherRow, column) => cellValue(other, otherRow, column)),
    )
  }

  /**
   * The truck's count for a weekly milk row: the drop's scope value from an
   * older draft, else what the delivery says. A milk row's Received box is the
   * meal's Started with, which never moves the receipt.
   */
  function milkTruckCount(row: RecordRow): number | null {
    const stored = scopedCount(row, 'received')
    return stored !== undefined ? stored : row.prefill.received.value
  }

  /**
   * A weekly milk scope's stored count from ANY step's edits, for a row that
   * joined after the value was typed elsewhere (its own step's store is written
   * through only from the moment it has a row of the scope). Undefined when no
   * step holds one.
   */
  function scopedCount(row: RecordRow, column: TransportCountColumn): number | null | undefined {
    if (!row.milk) {
      return undefined
    }
    for (const step of steps) {
      const stored = edits[step.key]?.rows[row.milk.storeKey]?.[column]
      if (stored !== undefined) {
        return stored
      }
    }
    return undefined
  }

  /** The drop's stored reading from ANY step's edits (see `scopedCount`). */
  function scopedTemp(row: RecordRow, column: TempColumn): number | null | undefined {
    if (!row.milk || column === 'servingTemp') {
      return undefined
    }
    const key = tempStoreKeyFor(row, column)
    for (const step of steps) {
      const stored = edits[step.key]?.temps[key]
      if (stored !== undefined) {
        return stored
      }
    }
    return undefined
  }

  const progressFor = (step: RecordStep) => stepProgress(step, (row, column) => cellValue(step, row, column))

  /**
   * The school edited a cell of this row — typed, scanned or reset — this
   * session or in the draft it reopened. Nothing but such an edit creates a
   * row's entry (`seedProductionEdits` seeds record bands only), and the entry
   * rides in the draft, which is what the session-only `touched` set cannot do.
   * A weekly milk row's transport counts live on its SKU's store row instead.
   */
  function rowEdited(step: RecordStep, row: RecordRow): boolean {
    const rows = edits[step.key]?.rows
    return rows?.[row.key] !== undefined || (row.milk !== null && rows?.[row.milk.storeKey] !== undefined)
  }

  /** Every step that has a row of this weekly milk row's scope: the SKU's counts and the drop's readings are written through to each. */
  function scopeSteps(step: RecordStep, row: RecordRow): RecordStep[] {
    if (!row.milk) {
      return [step]
    }
    const dropKey = row.milk.dropKey
    const hits = steps.filter((other) => other.rows.some((r) => r.milk?.dropKey === dropKey))
    return hits.length > 0 ? hits : [step]
  }

  // -- Edit helpers -----------------------------------------------------------
  function touch(cellId: string): void {
    setTouched((prev) => (prev.has(cellId) ? prev : new Set(prev).add(cellId)))
  }

  function clearScanMarks(cellId: string, scopeKey: string | null): void {
    setScanFlags((prev) => {
      if (!prev.has(cellId)) {
        return prev
      }
      const next = new Set(prev)
      next.delete(cellId)
      return next
    })
    // A hand-typed reading is a hand entry: it loses its PHOTO provenance.
    if (scopeKey !== null) {
      setScanProvenance((prev) => {
        if (!(scopeKey in prev)) {
          return prev
        }
        const next = { ...prev }
        delete next[scopeKey]
        return next
      })
    }
  }

  function patchStep(stepKey: string, patch: (pr: ProductionEdits[string]) => ProductionEdits[string]): void {
    dirtyRef.current = true
    setEdits((prev) => {
      const pr = prev[stepKey] ?? { rows: {}, temps: {}, writeIns: [] }
      return { ...prev, [stepKey]: patch(pr) }
    })
  }

  /**
   * Ordered / Prepared / Received on one row. `null` = the school cleared the
   * box. A weekly milk row writes its SKU's store row on EVERY step that has a
   * row of the drop, so the same number shows on both bands and both mealtimes.
   */
  function setTransportCount(
    step: RecordStep,
    row: RecordRow,
    column: TransportCountColumn,
    value: number | null,
  ): void {
    if (column === 'ordered') {
      // Planned is read-only (2026-11-04): the order, or the figure the record already stores.
      return
    }
    if (column === 'received' && isPreparedRow(row)) {
      setPrepared(step, row, value)
      return
    }
    const cellId = countCellIdFor(step.key, row, column)
    touch(cellId)
    clearScanMarks(cellId, null)
    const storeKey = transportStoreKeyFor(row)
    for (const target of scopeSteps(step, row)) {
      patchStep(target.key, (pr) => {
        const current: ProductionRowEdit = pr.rows[storeKey] ?? { ...ZERO_COUNTS }
        return { ...pr, rows: { ...pr.rows, [storeKey]: { ...current, [column]: value } } }
      })
    }
  }

  /** One of the four interlocked counts: the row re-derives (`deriveProductionCounts`). */
  function updateRow(step: RecordStep, row: RecordRow, field: CountField, value: number): void {
    const bucket = row.key
    const cellId = countCellId(step.key, bucket, field)
    touch(cellId)
    clearScanMarks(cellId, null)
    manualFieldsRef.current.add(`${bucket}:${field}`)
    const manual = new Set<CountField>(COUNT_FIELDS.filter((f) => manualFieldsRef.current.has(`${bucket}:${f}`)))
    const planned = plannedFor(step, row)
    const current: ProductionCounts = edits[step.key]?.rows[bucket] ?? { ...ZERO_COUNTS }
    const { counts, violations } = deriveProductionCounts({ current, field, value, planned, manual })
    setRowViolations((prev) => ({ ...prev, [`${step.key}:${bucket}`]: violations }))
    patchStep(step.key, (pr) => ({ ...pr, rows: { ...pr.rows, [bucket]: { ...pr.rows[bucket], ...counts } } }))
  }

  /**
   * What a row's Served and Leftover add up to: the meal's Prepared on every
   * record band (Leftover = Prepared − Served), milk or food; else the order.
   */
  function plannedFor(step: RecordStep, row: RecordRow): number {
    if (isPreparedRow(row)) {
      return cellValue(step, row, 'received') ?? (isMilkStartRow(row) ? 0 : (row.service?.planned ?? 0))
    }
    return row.service?.planned ?? row.trucks.reduce((sum, t) => sum + t.plannedQuantity, 0)
  }

  /**
   * A band row's Prepared, on this meal alone; its counts re-derive
   * (`rederiveCountsForPrepared`). A cleared milk box re-derives against the
   * default it falls back to.
   */
  function setPrepared(step: RecordStep, row: RecordRow, value: number | null): void {
    const bucket = row.key
    const cellId = countCellId(step.key, bucket, 'received')
    touch(cellId)
    clearScanMarks(cellId, null)
    const effective =
      value ??
      (isMilkStartRow(row)
        ? milkStartCell(
            undefined,
            milkStartDefault(daySteps, step.key, row, (other, otherRow, column) => cellValue(other, otherRow, column)),
          ).value
        : row.prefill.received.value)
    // Derived INSIDE the patch, from the row as it stands then: a scan applies
    // its Prepared and its Served in one pass, and a render-time snapshot here
    // would let this re-derive overwrite the Served the scan just read.
    patchStep(step.key, (pr) => {
      const manual = new Set<CountField>(COUNT_FIELDS.filter((f) => manualFieldsRef.current.has(`${bucket}:${f}`)))
      const current: ProductionCounts = pr.rows[bucket] ?? { ...ZERO_COUNTS }
      const counts = rederiveCountsForPrepared({ current, prepared: effective, manual })
      return { ...pr, rows: { ...pr.rows, [bucket]: { ...pr.rows[bucket], ...counts, received: value } } }
    })
  }

  function resetRow(step: RecordStep, row: RecordRow): void {
    const bucket = row.key
    for (const f of COUNT_FIELDS) {
      manualFieldsRef.current.delete(`${bucket}:${f}`)
    }
    setScanFlags((prev) => {
      const next = new Set(prev)
      for (const f of COUNT_FIELDS) {
        next.delete(countCellId(step.key, bucket, f))
      }
      return next.size === prev.size ? prev : next
    })
    setRowViolations((prev) => ({ ...prev, [`${step.key}:${bucket}`]: [] }))
    // Milk is never "everything ordered was served": its row resets to blank
    // counts and keeps the meal's Started with.
    const seeded = isMilkStartRow(row) ? { ...ZERO_COUNTS } : seedProductionCounts(row.service?.planned ?? 0)
    patchStep(step.key, (pr) => ({
      ...pr,
      rows: { ...pr.rows, [bucket]: { ...pr.rows[bucket], ...seeded } },
    }))
  }

  /** A temperature: written to the row's SCOPE, so every row of the item follows (D-B4.2). */
  function setTemp(step: RecordStep, row: RecordRow, column: TempColumn, value: number | null): void {
    const cellId = tempCellIdFor(row, column)
    touch(cellId)
    clearScanMarks(cellId, column === 'receivedTemp' ? tempScopeKeyFor(row, column) : null)
    writeTemp(step, row, column, value)
  }

  /** The store write behind `setTemp` and a scan: the drop's Kitchen °F / Received °F go to every step of the drop. */
  function writeTemp(step: RecordStep, row: RecordRow, column: TempColumn, value: number | null): void {
    const storeKey = tempStoreKeyFor(row, column)
    const targets = column === 'servingTemp' ? [step] : scopeSteps(step, row)
    for (const target of targets) {
      patchStep(target.key, (pr) => ({ ...pr, temps: { ...pr.temps, [storeKey]: value } }))
    }
  }

  function addWriteIn(prId: string, kind: ProductionWriteInEdit['kind']): void {
    const row: ProductionWriteInEdit = {
      id: null,
      key: crypto.randomUUID(),
      kind,
      menuItemId: null,
      menuItemName: null,
      description: '',
      quantity: 0,
      reimbursableQty: 0,
      nonReimbursableQty: 0,
      reimbursableTouched: false,
    }
    patchStep(prId, (pr) => ({ ...pr, writeIns: [...pr.writeIns, row] }))
  }

  function updateWriteIn(prId: string, key: string, patch: Partial<ProductionWriteInEdit>): void {
    patchStep(prId, (pr) => ({
      ...pr,
      writeIns: pr.writeIns.map((w) => {
        if (w.key !== key) {
          return w
        }
        const next = { ...w, ...patch }
        // Same claim-split interlock as the Service records task.
        if ('reimbursableQty' in patch) {
          next.reimbursableTouched = true
          next.nonReimbursableQty = Math.max(0, next.quantity - next.reimbursableQty)
        } else if ('nonReimbursableQty' in patch) {
          next.reimbursableTouched = true
          next.reimbursableQty = Math.max(0, next.quantity - next.nonReimbursableQty)
        } else if ('quantity' in patch && next.kind !== 'DROPPED') {
          if (next.reimbursableTouched) {
            next.nonReimbursableQty = Math.min(next.nonReimbursableQty, next.quantity)
            next.reimbursableQty = Math.max(0, next.quantity - next.nonReimbursableQty)
          } else {
            next.reimbursableQty = next.quantity
            next.nonReimbursableQty = 0
          }
        }
        return next
      }),
    }))
  }

  function removeWriteIn(prId: string, key: string): void {
    patchStep(prId, (pr) => ({ ...pr, writeIns: pr.writeIns.filter((w) => w.key !== key) }))
  }

  /**
   * Apply a scan's count patches in ONE pass against the LATEST edits:
   * `updateRow` replaces a row from a render-time snapshot, so a per-patch loop
   * would make every patch after the first overwrite the one before it.
   */
  function applyScanCountPatches(
    step: RecordStep,
    patches: ReadonlyArray<{ rowKey: string; field: CountField; value: number; planned: number }>,
  ): void {
    if (patches.length === 0) {
      return
    }
    const violations: Record<string, CountViolation[]> = {}
    patchStep(step.key, (pr) => {
      const working: Record<string, ProductionCounts> = {}
      for (const patch of patches) {
        manualFieldsRef.current.add(`${patch.rowKey}:${patch.field}`)
        const manual = new Set<CountField>(
          COUNT_FIELDS.filter((f) => manualFieldsRef.current.has(`${patch.rowKey}:${f}`)),
        )
        const current = working[patch.rowKey] ?? pr.rows[patch.rowKey] ?? { ...ZERO_COUNTS }
        const derived = deriveProductionCounts({
          current,
          field: patch.field,
          value: patch.value,
          planned: patch.planned,
          manual,
        })
        working[patch.rowKey] = derived.counts
        violations[`${step.key}:${patch.rowKey}`] = derived.violations
      }
      const rows = { ...pr.rows }
      for (const [rowKey, counts] of Object.entries(working)) {
        rows[rowKey] = { ...rows[rowKey], ...counts }
      }
      return { ...pr, rows }
    })
    setRowViolations((prev) => ({ ...prev, ...violations }))
  }

  // -- The Delivery and Service cards' fields ---------------------------------
  const profileName = [profile?.user?.firstName, profile?.user?.lastName].filter(Boolean).join(' ')

  function receiverNameFor(step: RecordStep): string {
    const stored = edits[step.key]?.receiverName
    if (stored !== undefined) {
      return stored
    }
    // A truck still out is received by whoever is signed in; a truck already in
    // shows who received it.
    if (step.trucks.some((truck) => truck.status === 'NEEDS_RECEIPT')) {
      return profileName
    }
    return step.trucks.find((truck) => truck.receiverName)?.receiverName ?? profileName
  }

  function deliveryTimeFor(step: RecordStep): string {
    const stored = edits[step.key]?.deliveryReadingTime
    if (stored !== undefined) {
      return stored
    }
    if (step.trucks.length > 0 && step.trucks.every((truck) => truck.status === 'RECEIVED')) {
      const at = step.trucks.find((truck) => truck.receivedAt)?.receivedAt ?? null
      const clock = readingTimeFromInstant(at)
      if (clock !== null) {
        return clock
      }
    }
    const latest =
      step.trucks
        .map((truck) => truck.deliveryDate)
        .sort()
        .at(-1) ?? serviceDate
    return defaultReadingTime(latest)
  }

  function serviceTimeFor(step: RecordStep): string {
    const override = serviceTimeOverride[step.key]
    if (override !== undefined) {
      return override
    }
    return (
      edits[step.key]?.readingTime ||
      readingTimeFromInstant(step.fill?.serviceReadingAt) ||
      defaultReadingTime(serviceDate)
    )
  }

  function setServiceTime(step: RecordStep, next: string): void {
    setServiceTimeOverride((prev) => ({ ...prev, [step.key]: next }))
    patchStep(step.key, (pr) => ({ ...pr, readingTime: next }))
  }

  // -- Scan -------------------------------------------------------------------
  const campusLabel = subtitle?.split('·')[0]?.trim() || locationId

  /**
   * Send one paper's photos to the reader. Returns the answer with the
   * worker's own photo numbers put back (the server numbers what it RECEIVED),
   * and any photo the device could not send, already phrased.
   */
  async function requestScan(
    files: File[],
    task: 'record' | 'delivery',
    rows: ScanRowWire[],
    dateLabel: string,
  ): Promise<{ result: ScanSheetResponse; errors: string[] }> {
    const prepared = await filesToImagePayloads(files)
    if (prepared.payloads.length === 0) {
      throw new Error(prepared.errors[0] ?? SCHOOL_SHEET_SCAN_COPY.errorFallback)
    }
    if (rows.length === 0) {
      throw new Error(SCHOOL_SHEET_SCAN_COPY.errorFallback)
    }
    let response: ScanSheetResponse
    try {
      response = await scanMutation.mutateAsync({
        schoolLocationId: locationId,
        serviceDate,
        task,
        contextLabel: campusLabel,
        // The calendar date(s), never the UI's "Today": the prompt prints this
        // label verbatim and rejects a photo whose printed date does not match.
        serviceDateLabel: dateLabel,
        images: prepared.payloads,
        rows,
      })
    } catch (err) {
      if (isScanAbort(err)) {
        throw new Error(SCAN_TIMEOUT_MESSAGE, { cause: err })
      }
      throw err
    }
    return { result: remapPhotoIndices(response, prepared.sourceIndices), errors: prepared.errors }
  }

  /** Every target's flat rows on one wire, with the index that routes each answer back to its step. */
  function scanTargets(
    targets: readonly RecordStep[],
    build: (step: RecordStep) => { rows: ScanRowWire[]; index: Map<string, RecordScanTarget> },
  ): { rows: ScanRowWire[]; index: Map<string, RecordScanTarget>; stepFor: (row: RecordRow) => RecordStep } {
    const rows: ScanRowWire[] = []
    const index = new Map<string, RecordScanTarget>()
    const stepOfRow = new Map<RecordRow, RecordStep>()
    for (const target of targets) {
      const built = build(target)
      rows.push(...built.rows)
      for (const [rowId, entry] of built.index) {
        index.set(rowId, entry)
        stepOfRow.set(entry.row, target)
      }
    }
    return { rows, index, stepFor: (row) => stepOfRow.get(row) ?? targets[0]! }
  }

  /** A scanned temperature onto its scope, with PHOTO provenance on a drop-off reading. */
  function applyScanTemps(temps: ReadonlyArray<TempScanPatch>): void {
    for (const patch of temps) {
      const step = stepsByKey.get(patch.stepKey)
      if (!step) {
        continue
      }
      touch(tempCellIdFor(patch.row, patch.column))
      writeTemp(step, patch.row, patch.column, patch.value)
      if (patch.column === 'receivedTemp') {
        setScanProvenance((prev) => ({ ...prev, [patch.scopeKey]: { source: 'PHOTO', confidence: patch.confidence } }))
      }
    }
  }

  /**
   * The production record's answer onto the rows: Received, the four counts,
   * both readings, the flags, and the sheet's time box (never over a time
   * already on screen). `deliveryTime: false` leaves the Delivery card's time
   * to the Orders sheet when one was read in the same pass.
   */
  function applyRecordResult(
    result: ScanSheetResponse,
    index: ReadonlyMap<string, RecordScanTarget>,
    stepFor: (row: RecordRow) => RecordStep,
    targets: readonly RecordStep[],
    opts: { deliveryTime: boolean },
  ): { summary: ScanPartialSummary; timeReadingApplied: boolean } {
    const applied = applyRecordScan(result, index, (row, column) => cellValue(stepFor(row), row, column))
    for (const patch of applied.received) {
      const step = stepsByKey.get(patch.stepKey)
      if (step) {
        setTransportCount(step, patch.row, 'received', patch.value)
      }
    }
    // A count patch names its record, and a record step's key IS its record id.
    const countsByStep = new Map<string, typeof applied.counts>()
    for (const patch of applied.counts) {
      countsByStep.set(patch.prId, [...(countsByStep.get(patch.prId) ?? []), patch])
    }
    for (const [stepKey, patches] of countsByStep) {
      const step = stepsByKey.get(stepKey)
      if (!step) {
        continue
      }
      applyScanCountPatches(
        step,
        patches.map((patch) => ({
          rowKey: patch.rowKey,
          field: patch.field,
          value: patch.value,
          planned: patch.planned,
        })),
      )
      for (const patch of patches) {
        touch(countCellId(step.key, patch.rowKey, patch.field))
      }
    }
    applyScanTemps(applied.temps)
    setScanFlags((prev) => nextScanFlags(prev, result, index, applied.flags))
    const scannedTime = parseTimeReading(result.timeReadingText)
    let timeReadingApplied = false
    if (scannedTime !== null) {
      for (const step of targets) {
        if (opts.deliveryTime && step.trucks.length > 0 && deliveryTimeFor(step) === '') {
          patchStep(step.key, (pr) => ({ ...pr, deliveryReadingTime: scannedTime }))
          timeReadingApplied = true
        }
        if (step.productionRecordId && serviceTimeFor(step) === '') {
          setServiceTime(step, scannedTime)
          timeReadingApplied = true
        }
      }
    }
    return { summary: applied.summary, timeReadingApplied }
  }

  /**
   * The Orders sheet's answer onto the rows (PROD-1511): a tick confirms the
   * Received on screen, an empty box flags it, the drop-off reading lands on
   * Received °F, and the sign-off's "Received by" and "Time:" fill the
   * Delivery card where the school has not typed one.
   */
  function applyDeliveryResult(
    result: ScanSheetResponse,
    index: ReadonlyMap<string, RecordScanTarget>,
    stepFor: (row: RecordRow) => RecordStep,
    targets: readonly RecordStep[],
  ): { summary: ScanPartialSummary; timeReadingApplied: boolean } {
    const applied = applyDeliveryScan(result, index, (row, column) => cellValue(stepFor(row), row, column))
    for (const patch of applied.received) {
      const step = stepsByKey.get(patch.stepKey)
      if (step) {
        setTransportCount(step, patch.row, 'received', patch.value)
      }
    }
    applyScanTemps(applied.temps)
    setScanFlags((prev) => nextScanFlags(prev, result, index, applied.flags))
    const receiver = receivedByFromScan(result.receivedByText)
    const scannedTime = parseTimeReading(result.timeReadingText)
    let timeReadingApplied = false
    for (const step of targets) {
      if (step.trucks.length === 0) {
        continue
      }
      // Whoever is signed in is only the default; a name on the paper is who actually received it.
      if (receiver !== null && edits[step.key]?.receiverName === undefined) {
        patchStep(step.key, (pr) => ({ ...pr, receiverName: receiver }))
      }
      if (scannedTime !== null && deliveryTimeFor(step) === '') {
        patchStep(step.key, (pr) => ({ ...pr, deliveryReadingTime: scannedTime }))
        timeReadingApplied = true
      }
    }
    return { summary: applied.summary, timeReadingApplied }
  }

  /**
   * Read the school's two papers against MANY steps (PROD-1511): the kitchen's
   * Orders sheet (`delivery`) and the production record (`record`). Both go out
   * together, one request per paper, every target's rows on each wire (row ids
   * are unique across steps and each row carries its mealtime, so the reader
   * tells Breakfast's Chicken bowl from Lunch's). The answers apply in a FIXED
   * order, the production record first and the Orders sheet second, so the
   * delivery paper's own facts (the drop-off reading, the Received tick) win
   * where both papers carry them (ASSUMED: PROD-1511 decision d4 is open). One
   * paper failing never discards the other: its error becomes a warning.
   */
  async function handleScanSheets(photos: SheetPhotos, targets: RecordStep[]): Promise<ScanApplySummary> {
    if (targets.length === 0 || (photos.record.length === 0 && photos.delivery.length === 0)) {
      throw new Error(SCHOOL_SHEET_SCAN_COPY.errorFallback)
    }
    const recordWire = photos.record.length > 0 ? scanTargets(targets, buildRecordScanRows) : null
    const deliveryWire = photos.delivery.length > 0 ? scanTargets(targets, buildDeliveryScanRows) : null
    const [recordRead, deliveryRead] = await Promise.allSettled([
      recordWire
        ? requestScan(photos.record, 'record', recordWire.rows, formatServiceDateLabel(serviceDate))
        : Promise.resolve(null),
      deliveryWire
        ? requestScan(photos.delivery, 'delivery', deliveryWire.rows, deliveryDatesLabel(targets, serviceDate))
        : Promise.resolve(null),
    ])
    const failures: Array<{ paper: string; reason: unknown }> = []
    if (recordRead.status === 'rejected') {
      failures.push({ paper: SHEET_PAPER_NAME.record, reason: recordRead.reason })
    }
    if (deliveryRead.status === 'rejected') {
      failures.push({ paper: SHEET_PAPER_NAME.delivery, reason: deliveryRead.reason })
    }
    const recordOk = recordRead.status === 'fulfilled' ? recordRead.value : null
    const deliveryOk = deliveryRead.status === 'fulfilled' ? deliveryRead.value : null
    if (!recordOk && !deliveryOk) {
      throw failures[0]?.reason instanceof Error ? failures[0].reason : new Error(SCHOOL_SHEET_SCAN_COPY.errorFallback)
    }

    const parts: ScanSheetPart[] = []
    if (recordOk && recordWire) {
      const applied = applyRecordResult(recordOk.result, recordWire.index, recordWire.stepFor, targets, {
        deliveryTime: deliveryOk === null,
      })
      parts.push({
        paper: SHEET_PAPER_NAME.record,
        summary: applied.summary,
        photoWarningCount: countPhotoWarnings(recordOk.result),
        deviceErrors: recordOk.errors,
        total: recordScanFieldTotal(recordWire.index),
        timeReadingApplied: applied.timeReadingApplied,
      })
    }
    if (deliveryOk && deliveryWire) {
      const applied = applyDeliveryResult(deliveryOk.result, deliveryWire.index, deliveryWire.stepFor, targets)
      parts.push({
        paper: SHEET_PAPER_NAME.delivery,
        summary: applied.summary,
        photoWarningCount: countPhotoWarnings(deliveryOk.result),
        deviceErrors: deliveryOk.errors,
        total: deliveryScanFieldTotal(deliveryWire.index),
        timeReadingApplied: applied.timeReadingApplied,
      })
    }
    return combineScanParts(parts, failures)
  }

  // -- The transport plan: which trucks get a receipt, and with what -----------

  /** One `receive-delivery` this step would post. */
  interface TransportSubmission {
    deliveryDate: string
    shipmentId: string
    lineQuantities: Array<{ shipmentLineId: string; receivedQuantity: number }>
    lineTemperatureLogs: Array<{
      shipmentLineId: string
      deliveryTemperatureF: number
      deliverySource: 'MANUAL' | 'PHOTO'
      deliveryConfidence?: 'high' | 'low'
      facilityTemperatureF?: number
      facilitySource?: 'MANUAL'
    }>
  }

  /** One line's reading on a receipt, with its provenance. */
  interface LineTemp {
    value: number
    source: 'MANUAL' | 'PHOTO'
    confidence: 'high' | 'low' | null
  }

  /**
   * Every truck this step would send, and whether anything on one is still
   * blank. DERIVED FROM THE ROWS THE TABLE SHOWS: a shipment reaches this plan
   * only through a row the school could see, check and correct.
   *
   * A PENDING truck is always sent. An already-received truck (on the wire or
   * on this screen, `receivedLocally`) is sent only when the school TOUCHED a
   * transport cell of a row that stands on it (D-B3.5): re-receiving is a
   * supported amendment, but posting an unchanged receipt would append a second
   * set of HACCP events that say nothing new.
   *
   * WEEKLY MILK: a drop's shipments are received WHOLE from whichever step
   * sends them first. A SKU's rows all show ONE scope value (never summed), a
   * SKU of the drop with no row on this step still fans the value typed on the
   * step it lives on, and every line of the drop rides under the drop's one
   * reading — so a receipt posted from Breakfast carries a temperature for the
   * chocolate line that only Lunch's record shows.
   */
  function buildTransportPlan(step: RecordStep): { sends: TransportSubmission[]; tempsMissing: boolean } {
    const sends: TransportSubmission[] = []
    let tempsMissing = false

    const qtyByLine = new Map<string, number>()
    const tempByLine = new Map<string, LineTemp>()
    const facilityByLine = new Map<string, number>()
    /** The drop's reading per weekly milk SHIPMENT: a line of the drop no row here lists still gets it. */
    const dropTempByShipment = new Map<string, LineTemp>()
    const missingTempLines = new Set<string>()
    const touchedShipments = new Set<string>()
    const visibleShipments = new Set<string>()

    // Per ITEM (per SKU scope for weekly milk): the band rows' Received sum fans
    // to the item's lines; the scope's readings ride on every line.
    const groups = new Map<string, RecordRow[]>()
    for (const row of step.rows) {
      if (row.trucks.length === 0) {
        continue
      }
      const key = row.milk ? row.milk.scopeKey : row.itemKey
      groups.set(key, [...(groups.get(key) ?? []), row])
    }
    for (const rows of groups.values()) {
      const lead = rows[0]!
      const parts = new Map<string, RecordRow['trucks'][number]>()
      for (const row of rows) {
        for (const truck of row.trucks) {
          parts.set(truck.shipmentLineId, truck)
          visibleShipments.add(truck.shipmentId)
        }
      }
      const lines = [...parts.values()]

      // The item's Received = Σ over its band rows of what each box SHOWS, a
      // CLEARED box counting as its prefill: clearing one band says "unknown"
      // for that band (its `/quantities` bucket is left unwritten), and unknown
      // cannot lower the truck's receipt below what the plan or the earlier
      // receipt already say for it — fanning the filled bands alone received a
      // 43-line truck at 23. Only a band with neither leaves the whole item to
      // the per-line fallback below. A weekly milk SKU's rows all show the ONE
      // scope value, so it is taken once, never summed. A sealed kit is ONE row
      // (PROD-1441), so its 146 packages count once, never once per recipe.
      const shown = rows.map((row) =>
        isMilkStartRow(row) ? milkTruckCount(row) : (cellValue(step, row, 'received') ?? row.prefill.received.value),
      )
      const total = lead.milk
        ? (shown[0] ?? null)
        : shown.every((value): value is number => value !== null)
          ? shown.reduce((sum, value) => sum + value, 0)
          : null
      if (total !== null) {
        // The box reads servings; the truck counts packages (`truckUnitScale`).
        const prepared = lead.truckUnitScale ? Math.round(total / lead.truckUnitScale) : total
        // A food item's Prepared is what the meal had ON HAND, which can hold
        // stock already at the school: the receipt is never above what the
        // truck brought, while a shortage still posts short. A milk SKU's
        // value is the truck's own count already (`milkTruckCount`).
        const packages = lead.milk
          ? prepared
          : truckReceiptFor(
              prepared,
              lines.reduce((sum, line) => sum + line.plannedQuantity, 0),
            )
        for (const share of distributeReceivedTotal(packages, lines)) {
          qtyByLine.set(share.shipmentLineId, share.receivedQuantity)
        }
      }

      const temp = cellValue(step, lead, 'receivedTemp')
      if (temp !== null && !Number.isNaN(temp)) {
        const mark = scanProvenance[tempScopeKeyFor(lead, 'receivedTemp')]
        const line: LineTemp = mark
          ? { value: temp, source: mark.source, confidence: mark.confidence }
          : { value: temp, source: 'MANUAL', confidence: null }
        for (const part of lines) {
          tempByLine.set(part.shipmentLineId, line)
          if (part.weeklyMilk) {
            dropTempByShipment.set(part.shipmentId, line)
          }
        }
      } else if (tempCheckRequired(lead.temperatureClass)) {
        for (const part of lines) {
          missingTempLines.add(part.shipmentLineId)
        }
      }

      // The KITCHEN reading ships ONLY when the school entered it: a
      // `facilityTemperatureF` on the wire writes a fresh FACILITY event and a
      // temperature log stamped MANUAL with the school's name on it, so
      // re-posting the chef's own prefill would duplicate the kitchen's reading.
      const kitchen = cellValue(step, lead, 'kitchenTemp')
      if (
        touched.has(tempCellIdFor(lead, 'kitchenTemp')) &&
        cellOffered(lead, 'kitchenTemp') &&
        kitchen !== null &&
        !Number.isNaN(kitchen) &&
        kitchen !== lead.prefill.kitchenTemp.value
      ) {
        for (const part of lines) {
          facilityByLine.set(part.shipmentLineId, kitchen)
        }
      }

      // A milk row's Received is the meal's Started with: typing it never re-sends the drop's receipt.
      const anyTouched =
        rows.some((row) => !isMilkStartRow(row) && touched.has(countCellIdFor(step.key, row, 'received'))) ||
        touched.has(tempCellIdFor(lead, 'receivedTemp')) ||
        touched.has(tempCellIdFor(lead, 'kitchenTemp'))
      if (anyTouched) {
        for (const part of lines) {
          touchedShipments.add(part.shipmentId)
        }
      }
    }

    // The other SKUs of a drop visible here: their scope value, typed on the
    // step they live on (or their prefill), fans onto their lines of the same
    // shipments; a touch there is a touch on the shipment.
    const dropsHere = new Set(step.rows.flatMap((row) => (row.milk ? [row.milk.dropKey] : [])))
    for (const other of steps) {
      if (other.key === step.key) {
        continue
      }
      for (const row of other.rows) {
        if (!row.milk || !dropsHere.has(row.milk.dropKey)) {
          continue
        }
        const milkLines = row.trucks.filter((truck) => truck.weeklyMilk)
        if (milkLines.length === 0 || milkLines.some((truck) => qtyByLine.has(truck.shipmentLineId))) {
          continue
        }
        const value = isMilkStartRow(row)
          ? milkTruckCount(row)
          : (cellValue(other, row, 'received') ?? row.prefill.received.value)
        if (value !== null) {
          for (const share of distributeReceivedTotal(value, milkLines)) {
            qtyByLine.set(share.shipmentLineId, share.receivedQuantity)
          }
        }
        if (!isMilkStartRow(row) && touched.has(countCellIdFor(other.key, row, 'received'))) {
          for (const truck of milkLines) {
            touchedShipments.add(truck.shipmentId)
          }
        }
      }
    }

    for (const entry of deliveriesByDate) {
      for (const delivery of entry.deliveries) {
        if (delivery.status === 'VOIDED' || !visibleShipments.has(delivery.shipmentId)) {
          continue
        }
        const alreadyReceived = delivery.status !== 'NEEDS_RECEIPT' || receivedLocally.has(delivery.shipmentId)
        if (alreadyReceived && !touchedShipments.has(delivery.shipmentId)) {
          continue
        }
        if (delivery.lines.some((line) => missingTempLines.has(line.shipmentLineId))) {
          tempsMissing = true
        }
        const dropTemp = dropTempByShipment.get(delivery.shipmentId)
        sends.push({
          deliveryDate: entry.deliveryDate,
          shipmentId: delivery.shipmentId,
          lineQuantities: delivery.lines.map((line) => ({
            shipmentLineId: line.shipmentLineId,
            receivedQuantity: qtyByLine.get(line.shipmentLineId) ?? line.receivedQuantity ?? line.plannedQuantity,
          })),
          lineTemperatureLogs: delivery.lines
            .map((line) => {
              const effective = tempByLine.get(line.shipmentLineId) ?? dropTemp
              if (effective == null) {
                return null
              }
              const facility = facilityByLine.get(line.shipmentLineId)
              return {
                shipmentLineId: line.shipmentLineId,
                deliveryTemperatureF: effective.value,
                deliverySource: effective.source,
                // Only a photo has a confidence to report; a MANUAL line sends
                // exactly the three keys it always has.
                ...(effective.confidence === null ? {} : { deliveryConfidence: effective.confidence }),
                ...(facility === undefined
                  ? {}
                  : { facilityTemperatureF: facility, facilitySource: 'MANUAL' as const }),
              }
            })
            .filter((x): x is NonNullable<typeof x> => x !== null),
        })
      }
    }

    return { sends, tempsMissing }
  }

  // -- Gates (active step) ----------------------------------------------------
  const active = activeStep
  /** A signed record step: its cells are read-only. A record-less step never locks (its receipts can be amended). */
  function stepLocked(step: RecordStep): boolean {
    return recordSigned(step)
  }
  const activeLocked = active !== null && stepLocked(active)
  const activeReceiptOnly = active !== null && receiptOnly(active)
  const plan = active ? buildTransportPlan(active) : { sends: [], tempsMissing: false }
  const receiverName = active ? receiverNameFor(active).trim().slice(0, 80) : ''
  const transportGateOk = plan.sends.length > 0 && !plan.tempsMissing && receiverName.length > 0

  // Every non-AMBIENT temperature on the step's rows is set: the Received °F of
  // every row that stands on a truck, and the Serving °F of every record row.
  const transportTempsSet =
    active !== null &&
    active.rows
      .filter((row) => row.trucks.length > 0 && cellOffered(row, 'receivedTemp'))
      .every((row) => cellValue(active, row, 'receivedTemp') !== null)
  const allServiceTempsSet =
    active !== null &&
    active.productionRecordId !== null &&
    active.rows
      .filter((row) => row.service !== null && cellOffered(row, 'servingTemp'))
      .every((row) => cellValue(active, row, 'servingTemp') !== null)
  const hasBlockingViolation =
    active !== null &&
    Object.entries(rowViolations).some(([key, list]) => key.startsWith(`${active.key}:`) && list.some(blocksSubmit))
  const hasIncompleteWriteIn = active !== null && (edits[active.key]?.writeIns ?? []).some(writeInRowIncomplete)
  const recordGateOk =
    active !== null &&
    active.productionRecordId !== null &&
    !activeLocked &&
    allServiceTempsSet &&
    transportTempsSet &&
    signatoryName.trim() !== '' &&
    // A change to a signed record states why (it prints on the USDA document).
    (!amend || amendReason.trim() !== '') &&
    !hasBlockingViolation &&
    !hasIncompleteWriteIn &&
    // Nothing to receive is fine; something to receive must be receivable.
    (plan.sends.length === 0 || transportGateOk)
  // A record-less step has nothing to sign: `Sign & complete` IS the receipts,
  // so it needs something to send, a receiver and every reading; no signature.
  const transportOnlyGateOk =
    active !== null && active.productionRecordId === null && transportGateOk && transportTempsSet
  // A signed record's late receipt: the truck's own gate plus a real arrival
  // time, since a past truck would otherwise be stamped with "now".
  const receiptOnlyGateOk =
    active !== null && activeReceiptOnly && transportGateOk && isReadingTime(deliveryTimeFor(active))
  const signGateOk = activeReceiptOnly
    ? receiptOnlyGateOk
    : active?.productionRecordId
      ? recordGateOk
      : transportOnlyGateOk

  /**
   * What keeps the gate shut, in words (PROD-1511): the phone lists these at
   * the top of the review and its main button reads "Fix N things". Built from
   * the SAME facts as the gate above; a shut gate with nothing listed gets the
   * catch-all, an open gate lists nothing.
   */
  const activeBlockers: StepBlocker[] = (() => {
    if (active === null || activeLocked || signGateOk) {
      return []
    }
    const hasRecord = active.productionRecordId !== null
    const receivedScopes = new Set<string>()
    const servingScopes = new Set<string>()
    const rows: StepBlockerRow[] = active.rows.map((row) => {
      let missingReceivedTemp = false
      if (
        row.trucks.length > 0 &&
        cellOffered(row, 'receivedTemp') &&
        cellValue(active, row, 'receivedTemp') === null
      ) {
        const id = tempCellIdFor(row, 'receivedTemp')
        missingReceivedTemp = !receivedScopes.has(id)
        receivedScopes.add(id)
      }
      let missingServingTemp = false
      if (
        hasRecord &&
        row.service !== null &&
        cellOffered(row, 'servingTemp') &&
        cellValue(active, row, 'servingTemp') === null
      ) {
        const id = tempCellIdFor(row, 'servingTemp')
        missingServingTemp = !servingScopes.has(id)
        servingScopes.add(id)
      }
      return {
        key: row.key,
        name: row.band ? `${row.label} ${row.band}` : row.label,
        blockingViolation: (rowViolations[`${active.key}:${row.key}`] ?? []).some(blocksSubmit),
        missingReceivedTemp,
        missingServingTemp,
      }
    })
    const listed = buildStepBlockers({
      rows,
      receiverMissing: plan.sends.length > 0 && receiverName.length === 0,
      signatureMissing: hasRecord && signatoryName.trim() === '',
      amendReasonMissing: hasRecord && Boolean(amend) && amendReason.trim() === '',
      incompleteWriteIn: hasIncompleteWriteIn,
      lineTempMissing: plan.sends.length > 0 && plan.tempsMissing && !rows.some((row) => row.missingReceivedTemp),
      nothingToConfirm: !hasRecord && plan.sends.length === 0,
    })
    return listed.length === 0 ? [OTHER_BLOCKER] : listed
  })()

  // -- Submit -----------------------------------------------------------------

  /** Post every receipt the plan calls for. Returns how many failed. */
  async function receiveTrucks(step: RecordStep, sends: TransportSubmission[]): Promise<number> {
    if (sends.length === 0) {
      return 0
    }
    const nowIso = new Date().toISOString()
    const time = deliveryTimeFor(step)
    const name = receiverNameFor(step).trim().slice(0, 80)
    const results = await Promise.allSettled(
      sends.map((send) =>
        receiveMutation.mutateAsync({
          schoolLocationId: locationId,
          shipmentId: send.shipmentId,
          arrivedAt: combineReadingInstant(send.deliveryDate, time) ?? nowIso,
          receiverName: name,
          lineQuantities: send.lineQuantities,
          lineTemperatureLogs: send.lineTemperatureLogs,
        }),
      ),
    )
    return results.filter((r) => r.status === 'rejected').length
  }

  /**
   * After a receipt lands: the sent shipments are received on this screen, the
   * cells that stand on them (on EVERY step — a weekly milk scope spans steps)
   * are no longer "touched", and a shipped-task draft for those dates is stale.
   */
  function settleReceipts(sends: TransportSubmission[]): void {
    const sentShipments = new Set(sends.map((send) => send.shipmentId))
    const sentDates = new Set(sends.map((send) => send.deliveryDate))
    setReceivedLocally((prev) => {
      const next = new Set(prev)
      for (const id of sentShipments) {
        next.add(id)
      }
      return next
    })
    setTouched((prev) => {
      const next = new Set(prev)
      for (const other of steps) {
        for (const row of other.rows) {
          if (!row.trucks.some((truck) => sentShipments.has(truck.shipmentId))) {
            continue
          }
          next.delete(countCellIdFor(other.key, row, 'received'))
          next.delete(tempCellIdFor(row, 'receivedTemp'))
          next.delete(tempCellIdFor(row, 'kitchenTemp'))
        }
      }
      return next
    })
    const receiveStore = useCafeteriaReceiveDraftStore.getState()
    for (const date of sentDates) {
      receiveStore.clearDraft(receiveDraftKey(locationId, date))
    }
    void queryClient.invalidateQueries({ queryKey: ['cafeteria', 'deliveries'] })
  }

  /**
   * Whether anything is left to do on the OTHER steps once this one finished:
   * an unsigned record, or a record-less step with a truck still out. The
   * dialog closes only when nothing is.
   */
  function anythingRemaining(step: RecordStep, justReceived: ReadonlySet<string>): boolean {
    return steps.some((other) => {
      if (other.key === step.key) {
        return false
      }
      const truckOut = other.trucks.some((truck) => !truckReceived(truck) && !justReceived.has(truck.shipmentId))
      if (other.productionRecordId) {
        return (!other.signed && !(other.productionRecordId in signedLocally)) || truckOut
      }
      return truckOut
    })
  }

  /** The quantities payload: every band's counts plus the school's entered Ordered / Prepared / Received (§5.6). */
  function quantitiesFor(step: RecordStep, mt: ProductionFillMealtime): SaveQuantitiesInput['lines'] {
    const rowsByKey = new Map(step.rows.map((row) => [row.key, row] as const))
    const lines: SaveQuantitiesInput['lines'] = mt.deliverables.flatMap((deliverable) =>
      distributeProductionFill({
        lineTargets: deliverable.lineTargets,
        gradeRows: deliverable.gradeRows.map((gradeRow) => {
          const row = rowsByKey.get(productionFillRowKey(deliverable.deliverableKey, gradeRow))
          if (!row) {
            return gradeRow
          }
          // A weekly milk row's Prepared is the TRUCK's one scope value, not
          // this band's figure: stamped on every band's bucket the document
          // (which sums ENTERED buckets per line) would print it twice, so it
          // stays off the record and persists through the receipt. Its Ordered
          // and Received (the Started with) are the band's own for this meal,
          // so they are saved on the band (Amana 2026-11-02: breakfast 179,
          // lunch 141).
          const transport = row.milk
            ? {
                ordered: isMilkStartRow(row) ? cellValue(step, row, 'ordered') : null,
                prepared: null,
                received: isMilkStartRow(row) ? cellValue(step, row, 'received') : null,
              }
            : {
                ordered: cellValue(step, row, 'ordered'),
                prepared: cellValue(step, row, 'prepared'),
                received: cellValue(step, row, 'received'),
              }
          return {
            ...gradeRow,
            served: cellValue(step, row, 'served') ?? gradeRow.served,
            reimbursable: cellValue(step, row, 'reimbursable') ?? gradeRow.reimbursable,
            nonReimbursable: cellValue(step, row, 'nonReimbursable') ?? gradeRow.nonReimbursable,
            leftover: cellValue(step, row, 'leftover') ?? gradeRow.leftover,
            ...transport,
          }
        }),
      }),
    )
    // A truck line the record knows by identity but the fill has no deliverable
    // for: its counts land on one tier-less bucket of that line — but ONLY once
    // the school edited the row. Its Ordered and Received are pre-filled from
    // the truck on every load, so "any value present" would post a zero-served
    // bucket for a line nobody looked at, on every signature, and mint a row of
    // zeros on the document.
    for (const row of step.rows) {
      if (!row.fallbackIdentity || row.service !== null || !rowEdited(step, row)) {
        continue
      }
      const served = cellValue(step, row, 'served')
      // Same rule as above: a weekly milk SKU's scope counts stay off the record.
      const ordered = row.milk ? null : cellValue(step, row, 'ordered')
      const prepared = row.milk ? null : cellValue(step, row, 'prepared')
      const received = row.milk ? null : cellValue(step, row, 'received')
      if (served === null && ordered === null && prepared === null && received === null) {
        continue
      }
      lines.push({
        bundleDeliverableId: row.fallbackIdentity.bundleDeliverableId,
        menuItemId: row.fallbackIdentity.menuItemId,
        buckets: [
          {
            gradeRangeId: null,
            groupDimension: null,
            groupValue: null,
            servedQty: served ?? 0,
            reimbursableQty: cellValue(step, row, 'reimbursable') ?? 0,
            nonReimbursableQty: cellValue(step, row, 'nonReimbursable') ?? 0,
            leftoverQty: cellValue(step, row, 'leftover') ?? 0,
            ...(ordered === null ? {} : { orderedQty: ordered }),
            ...(prepared === null ? {} : { preparedQty: prepared }),
            ...(received === null ? {} : { receivedQty: received }),
          },
        ],
      })
    }
    return lines
  }

  /**
   * The RECORD-scoped readings of a step: one entry per truck-less item whose
   * column holds a value the school stands behind (typed, scanned, or a prefill
   * that is not already a `ShipmentEvent` on the document). Items WITH truck
   * lines send their readings on the receipt instead.
   */
  function recordScopedTemps(
    step: RecordStep,
    column: 'kitchenTemp' | 'receivedTemp',
  ): SaveServiceTempsInput['temperatures'] {
    const entries: SaveServiceTempsInput['temperatures'] = []
    const seenScopes = new Set<string>()
    for (const row of step.rows) {
      if (row.trucks.length > 0 || !cellOffered(row, column) || seenScopes.has(row.tempScopeKey)) {
        continue
      }
      seenScopes.add(row.tempScopeKey)
      const value = cellValue(step, row, column)
      if (value === null || Number.isNaN(value)) {
        continue
      }
      const prefill = row.prefill[column]
      if (!touched.has(tempCellIdFor(row, column)) && prefill.fromShipmentEvent) {
        continue
      }
      if (row.service) {
        entries.push(...serviceTempSaveEntries(row.service.deliverable, value))
      } else if (row.fallbackIdentity) {
        entries.push({ ...row.fallbackIdentity, value })
      }
    }
    return entries
  }

  async function handleSignAndComplete(): Promise<void> {
    if (!active) {
      return
    }
    const step = active
    setPickedKey(step.key)
    setSubmitting(true)
    // The trucks first: a record signed over an unreceived shipment is a record
    // whose Received column the server never learned.
    const failed = await receiveTrucks(step, plan.sends)
    if (failed > 0) {
      setSubmitting(false)
      toast.error(`${failed} deliver${failed === 1 ? 'y' : 'ies'} could not be confirmed.`)
      return
    }
    const justReceived = new Set(plan.sends.map((send) => send.shipmentId))
    if (!step.productionRecordId || !step.fill || receiptOnly(step)) {
      // A record-less step, or a signed record's late receipt: the receipts ARE
      // the step, and it is done once they land. No auto-advance; the dialog
      // closes only when nothing else is left.
      settleReceipts(plan.sends)
      setSubmitting(false)
      toast.success('Deliveries confirmed.')
      if (!anythingRemaining(step, justReceived)) {
        onComplete()
      }
      return
    }
    const prId = step.productionRecordId
    const mt = step.fill
    // Amending: the one reason rides on every write the server records as an amendment.
    const reason = amend ? amendReason.trim() : null
    try {
      await saveQuantities.mutateAsync({ productionRecordId: prId, lines: quantitiesFor(step, mt), reason })
      await saveWriteIns.mutateAsync({
        productionRecordId: prId,
        writeIns: writeInSaveEntries(edits[step.key]?.writeIns ?? []),
        reason,
      })

      // SERVICE readings: every deliverable's scope, plus a transport-only row
      // the document knows. ONE post: the server replaces the checkpoint's rows.
      const serviceEntries: SaveServiceTempsInput['temperatures'] = []
      const servingScopes = new Set<string>()
      for (const row of step.rows) {
        if (!cellOffered(row, 'servingTemp') || servingScopes.has(row.tempScopeKey)) {
          continue
        }
        servingScopes.add(row.tempScopeKey)
        const value = cellValue(step, row, 'servingTemp')
        if (value === null || Number.isNaN(value)) {
          continue
        }
        if (row.service) {
          serviceEntries.push(...serviceTempSaveEntries(row.service.deliverable, value))
        } else if (row.fallbackIdentity) {
          serviceEntries.push({ ...row.fallbackIdentity, value })
        }
      }
      const serviceTime = serviceTimeFor(step)
      if (serviceEntries.length > 0) {
        await saveTemps.mutateAsync({
          productionRecordId: prId,
          temperatures: serviceEntries,
          checkpoint: 'SERVICE',
          readingAt: combineReadingInstant(serviceDate, serviceTime),
        })
      }

      // Record-scoped DELIVERY / FACILITY readings for truck-less items.
      const deliveryEntries = recordScopedTemps(step, 'receivedTemp')
      if (deliveryEntries.length > 0) {
        const latestDelivery =
          step.trucks
            .map((truck) => truck.deliveryDate)
            .sort()
            .at(-1) ?? serviceDate
        await saveTemps.mutateAsync({
          productionRecordId: prId,
          temperatures: deliveryEntries,
          checkpoint: 'DELIVERY',
          readingAt: combineReadingInstant(latestDelivery, deliveryTimeFor(step)),
        })
      }
      const facilityEntries = recordScopedTemps(step, 'kitchenTemp')
      if (facilityEntries.length > 0) {
        await saveTemps.mutateAsync({ productionRecordId: prId, temperatures: facilityEntries, checkpoint: 'FACILITY' })
      }

      const signedByName = signatoryName.trim()
      await sign.mutateAsync({ productionRecordId: prId, signedByName, reason })
      settleReceipts(plan.sends)
      setSignedLocally((prev) => ({ ...prev, [prId]: { name: signedByName, signedAt: new Date().toISOString() } }))
      void queryClient.invalidateQueries({ queryKey: ['production-records', 'document', prId] })
      // This record's entries are on the server now; the other records' drafts stay.
      const persisted: ProductionEdits = {}
      for (const other of mealtimes) {
        const pr = edits[other.productionRecordId]
        if (
          pr &&
          other.productionRecordId !== prId &&
          !isSignedStatus(other.status) &&
          !signedLocally[other.productionRecordId]
        ) {
          persisted[other.productionRecordId] = pr
        }
      }
      useCafeteriaProductionDraftStore.getState().saveDraft(draftKey, persisted)

      if (anythingRemaining(step, justReceived)) {
        toast.success(`${step.title} signed.`)
      } else {
        toast.success(amend ? 'Production record amended.' : 'Production record signed.')
        onComplete()
      }
    } catch (err) {
      // The server names its refusal (`{ error, message }`); show that, because
      // the worker cannot fix what the toast does not name.
      toast.error(
        err instanceof ApiError
          ? err.message
          : 'Could not complete the record. Check the entered numbers and try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  // -- Render -----------------------------------------------------------------
  // Gated on the fill alone, Breakfast rendered by itself and Lunch (whose
  // record did not exist yet) popped in seconds later while Breakfast's cells
  // re-shaped under the user as its document landed (Burris, Sep 21).
  const loading = readsPending && !readsSettled
  const busy = submitting

  /**
   * PROD-1441: a row that served more than it received. Schools serve from
   * backstock (milk left from an earlier delivery), so this is a NOTE, never
   * a blocker and never marked on the signed document. Both figures must be
   * on screen for it to speak.
   */
  function servedOverReceived(step: RecordStep, row: RecordRow): boolean {
    if (!cellOffered(row, 'served')) {
      return false
    }
    const served = cellValue(step, row, 'served')
    const received = cellValue(step, row, 'received')
    return served !== null && received !== null && served > received
  }

  /**
   * The phone's Served-above-Received note (PROD-1511): both numbers, why it
   * happened when Served is still the every-meal default, that backstock
   * explains it, and a one-tap fix that sets Served to what was received
   * when it does not (the row re-derives as any typed Served does).
   */
  function servedOverReceivedNote(step: RecordStep, row: RecordRow): ReactNode {
    const served = cellValue(step, row, 'served') ?? 0
    const received = cellValue(step, row, 'received') ?? 0
    const defaulted =
      row.service?.gradeRow?.servedDefaulted === true && !touched.has(countCellId(step.key, row.key, 'served'))
    return (
      <div
        data-served-exceeds-received
        className="space-y-2 rounded-lg bg-amber-50 p-3 text-[13px] leading-[19px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        <p>
          <span className="font-semibold">
            Served ({served}) is more than Prepared ({received}).
          </span>{' '}
          {defaulted ? `Served starts at all ${served} meals, but ${received} are marked prepared. ` : null}
          {BACKSTOCK_NOTE}
        </p>
        <Button
          type="button"
          variant="outline"
          className="h-11 rounded-full border-amber-300 bg-background px-4 text-amber-900 dark:border-amber-800 dark:text-amber-200"
          disabled={busy || stepLocked(step)}
          onClick={() => updateRow(step, row, 'served', received)}>
          Set Served to {received}
        </Button>
      </div>
    )
  }

  /**
   * A row's footer: its count violations and the Reset link. The phone's cards
   * also say Served exceeds received here; the desktop table says it once,
   * above the table, and marks the Served box (`inlineOverReceived` false).
   */
  function violationsNode(step: RecordStep, row: RecordRow, inlineOverReceived = true): ReactNode {
    const violations = rowViolations[`${step.key}:${row.key}`] ?? []
    const overReceived = inlineOverReceived && servedOverReceived(step, row)
    if (violations.length === 0 && !overReceived) {
      return null
    }
    return (
      <div className="space-y-1">
        {violations.map((violation) => (
          <p
            key={violation.kind}
            className={blocksSubmit(violation) ? 'text-xs text-destructive' : 'text-xs text-amber-600'}>
            {countViolationMessage(violation)}
          </p>
        ))}
        {overReceived ? servedOverReceivedNote(step, row) : null}
        <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={() => resetRow(step, row)}>
          Reset row
        </Button>
      </div>
    )
  }

  /**
   * One row's view. `surface` is who renders it: the phone's cards keep the
   * Served-exceeds-received line under the row; the desktop table says it once
   * above the table (PROD-1441, the kit outline).
   */
  function rowView(
    step: RecordStep,
    row: RecordRow,
    locked: boolean,
    surface: 'table' | 'phone' = 'phone',
  ): RecordSpreadsheetRowView {
    const name = row.band ? `${row.label} ${row.band}` : row.label
    const overReceived = servedOverReceived(step, row)
    const cells = {} as Record<RecordColumnKey, RecordCell>
    // A signed record's late receipt opens only what the pending truck brought.
    const pending = locked && receiptOnly(step) ? pendingShipmentIds(step) : null
    const receiptOpen = pending !== null && row.trucks.some((truck) => pending.has(truck.shipmentId))
    for (const column of RECORD_COLUMNS) {
      const cellLocked = locked && !(receiptOpen && (column === 'received' || column === 'receivedTemp'))
      const value = cellValue(step, row, column)
      const offered = cellOffered(row, column)
      const cellId = cellIdFor(step.key, row, column)
      // A milk row's Prepared is the meal's on-hand milk: its own tip (an
      // earlier meal's leftover, this week's carry, the delivery, or the saved number).
      const milkStart = column === 'received' && isMilkStartRow(row)
      const prefill = milkStart ? milkStartFor(step, row) : row.prefill[column]
      const hint = prefill.value !== null && prefill.tip ? prefill.tip : null
      const tip = hint ? <CafeteriaModeTip {...hint} /> : null
      const ariaLabel = `${name} ${INPUT_LABEL[column]}`
      const flag = scanFlags.has(cellId) ? ('check' as const) : undefined
      // Still Ordo's assumption (PROD-1511): untouched, unchanged, and either a
      // number from the order / delivery plan / kitchen, or a service count the
      // server defaulted to every meal ordered.
      const prefilled =
        value !== null &&
        !touched.has(cellId) &&
        value === prefill.value &&
        (isServiceCountColumn(column)
          ? row.service?.gradeRow?.servedDefaulted === true
          : prefillIsAssumption(prefill.tip))
      if (isTempColumn(column)) {
        cells[column] = {
          kind: 'temp',
          offered: tempCheckRequired(row.temperatureClass),
          value,
          ariaLabel,
          tip,
          hint,
          alert: offered && outsideHoldingBand(value, row.temperatureClass),
          prefilled: offered && prefilled,
          flag,
          disabled: busy || cellLocked || !offered,
          title: offered ? undefined : NOT_ON_RECORD_TITLE,
          onChange: (next) => setTemp(step, row, column, next),
        }
        continue
      }
      cells[column] = {
        kind: 'count',
        value: value ?? '',
        ariaLabel,
        tip,
        hint,
        note: column === 'served' && overReceived,
        prefilled: offered && prefilled,
        caption: column === 'served' && offered && prefilled ? FULL_ORDER_CAPTION : undefined,
        flag,
        // Planned is the order (or the figure the record stores): shown, never typed (2026-11-04).
        ...(column === 'ordered' ? { readOnly: true } : {}),
        disabled: busy || cellLocked || !offered,
        title: offered ? undefined : NOT_ON_RECORD_TITLE,
        onValueChange: (next) => {
          if (isTransportCountColumn(column)) {
            setTransportCount(step, row, column, next === '' ? null : Number(next))
          } else if (isServiceCountColumn(column)) {
            updateRow(step, row, column, Number(next))
          }
        },
      }
    }
    const kitComponents = row.kit?.components ?? []
    const kit = kitComponents.length > 0
    return {
      key: row.key,
      label: row.label,
      legend: row.legend,
      // The table names a sealed kit by its main recipe and lists every recipe under it (PROD-1441).
      title: kit ? kitDisplayTitle(row.label, kitComponents) : undefined,
      badge: kit ? kitBadge(kitComponents.length) : undefined,
      meta: kit ? KIT_COUNTS_NOTE : undefined,
      temperatureClass: row.temperatureClass,
      cells,
      footer: row.service ? violationsNode(step, row, surface === 'phone') : null,
      // A sealed kit's recipes ride under the row as count-less sub-rows (PROD-1441). On the table the
      // serving sits beside the name, the portion alone like every item's legend (`0.75 cup`); the phone
      // card sets it on a line of its own, so it names it.
      subRows: row.kit?.components.map((component) => ({
        key: component.componentId,
        name: component.name,
        legend: component.servingSize
          ? surface === 'table'
            ? component.servingSize
            : `Serving ${component.servingSize}`
          : '',
      })),
    }
  }

  /**
   * The step's rows as the paper lays them out (PROD-1441): grade-group
   * blocks, then main choices / shared sides and milk / condiments, each row a
   * row view; and per block the reimbursable meals claimed — the main choices'
   * Served / Reimb / Non-reimb as the cells show them — or null when the block
   * has no main.
   */
  function groupsFor(
    step: RecordStep,
    locked: boolean,
    surface: 'table' | 'phone' = 'phone',
  ): RecordSpreadsheetGroupView[] {
    // The record's own document says how the meal is served (the section
    // captions) and which meal pattern its menu was planned for.
    const doc = step.productionRecordId ? (documents.get(step.productionRecordId) ?? null) : null
    const serviceStyle = doc?.serviceStyle ?? null
    const mealPattern = documentMealPattern(doc)
    return groupRecordRows(step.rows).map((group) => {
      const mains = group.sections.find((section) => section.key === 'MAIN')?.rows ?? []
      // Per MEAL, as the cells show them: a meal's two mains are one meal (`claimedMeals`).
      const claimRows: MealClaimRow[] = mains.map((row) => ({
        mealKeys: recordRowMealKeys(row),
        served: cellValue(step, row, 'served'),
        reimbursable: cellValue(step, row, 'reimbursable'),
        nonReimbursable: cellValue(step, row, 'nonReimbursable'),
      }))
      const together = mainsServedTogether(claimRows)
      return {
        key: group.key,
        label: group.band,
        pattern: mealPattern && mealPattern !== group.band ? `${mealPattern} meal pattern` : null,
        sections: group.sections.map((section) => {
          const servedTogether = section.key === 'MAIN' && together
          return {
            key: section.key,
            title: servedTogether ? SERVED_TOGETHER_TITLE : section.title,
            caption:
              servedTogether && serviceStyle === 'OVS'
                ? SERVED_TOGETHER_CAPTION
                : sectionCaption(section.key, serviceStyle),
            rows: section.rows.map((row) => rowView(step, row, locked, surface)),
          }
        }),
        claimed: mains.length === 0 ? null : claimedMeals(claimRows),
      }
    })
  }

  const railSteps = steps.map((step) => {
    const done = stepDone(step)
    const { filled, total } = progressFor(step)
    return { key: step.key, title: step.title, done, filled, total }
  })

  // The phone: photograph the sheets, read them once for every unsigned
  // mealtime, review each as cards, confirm. Same state, same chain, a
  // different tree (plan-06 lane C); the desktop below is untouched.
  if (phone) {
    return (
      <MobileRecordWizard
        subtitle={subtitle ? taskDateFirstLabel(campusLabel, serviceDate) : undefined}
        loading={loading}
        busy={busy}
        steps={steps}
        stepDone={stepDone}
        activeStepKey={active?.key ?? null}
        onSelectStep={selectStep}
        groupsFor={(step) => groupsFor(step, stepLocked(step))}
        onScan={(photos, targets) => handleScanSheets(photos, targets)}
        blockers={activeBlockers}
        signGateOk={signGateOk}
        onConfirm={() => handleSignAndComplete()}
        onExit={onExit}
        renderStepCards={(step) => stepCards(step, stepLocked(step))}
        amend={amend !== null}
      />
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col bg-background">
      <CafeteriaWorkflowHeader
        title={amend ? 'Amend production record' : 'Production record'}
        subtitle={subtitle}
        actions={
          <Button type="button" variant="outline" onClick={onExit}>
            {amend ? 'Cancel' : 'Save for later'}
          </Button>
        }
      />

      {/* Mobile: mealtimes as a scrollable chip strip above the step. Skeleton
          chips while the reads land, so a partial rail never flashes here. */}
      {loading ? (
        <div className="flex gap-2 border-b px-4 py-3 md:hidden" aria-busy="true">
          <Skeleton className="h-[38px] w-40 rounded-full" />
          <Skeleton className="h-[38px] w-40 rounded-full" />
        </div>
      ) : active && !amend ? (
        <div className="border-b px-4 py-3 md:hidden">
          <MealtimeStepList orientation="horizontal" steps={railSteps} selectedKey={active.key} onSelect={selectStep} />
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        {/* Desktop: mealtimes as a left rail. An amendment is one record: no rail. */}
        {amend ? null : (
          <aside className="hidden w-72 shrink-0 overflow-y-auto border-r p-3 md:block">
            <p className="px-3 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Mealtimes</p>
            {loading ? (
              <div className="space-y-2 px-3" aria-busy="true">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
            ) : active ? (
              <MealtimeStepList
                orientation="vertical"
                steps={railSteps}
                selectedKey={active.key}
                onSelect={selectStep}
              />
            ) : null}
          </aside>
        )}

        {/* Full width: eleven columns have nowhere to go inside a reading measure. */}
        <main className="min-w-0 flex-1 overflow-y-auto px-4 pb-10 pt-6 md:px-8">
          {loading ? (
            <div className="space-y-4" aria-busy="true" aria-label="Loading production record">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-96" />
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-64 w-full rounded-lg" />
            </div>
          ) : !active ? (
            <div className="rounded-lg border border-dashed bg-background p-6 text-center text-sm text-muted-foreground">
              No production records have been generated for this day yet.
            </div>
          ) : (
            renderStep(active, activeLocked)
          )}
        </main>
      </div>
    </div>
  )

  /**
   * One mealtime step, top to bottom per image-18. A plain render function,
   * NOT a nested component: a component defined inside the flow is a new type
   * on every render, so React would remount the whole step and drop the focus
   * out of the box being typed in.
   */
  function renderStep(step: RecordStep, locked: boolean): ReactNode {
    const prId = step.productionRecordId
    const hasTrucks = step.trucks.length > 0
    const showScan = step.rows.length > 0 && !locked
    // Every grade group titles its own table below, one band or several alike;
    // the mealtime title carries only how the meal is served.
    const groups = groupsFor(step, locked, 'table')
    const doc = prId ? (documents.get(prId) ?? null) : null
    const serviceStyle = doc ? serviceStyleDisplay(doc).label : null
    // Served above Received, said once for the step; the table marks each Served box.
    // A note, not a blocker: the extra usually came from backstock.
    const overHeadline = servedOverReceivedHeadline(
      groupRecordRows(step.rows).map((group) => ({
        band: group.band,
        count: group.sections.reduce(
          (sum, section) => sum + section.rows.filter((row) => servedOverReceived(step, row)).length,
          0,
        ),
      })),
    )
    return (
      <>
        <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
          <h2 className="text-2xl font-semibold tracking-normal">{step.title}</h2>
          {serviceStyle ? <span className="text-[13px] text-muted-foreground">{serviceStyle}</span> : null}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {amend
            ? AMEND_DESCRIPTION
            : locked && receiptOnly(step)
              ? RECEIPT_ONLY_DESCRIPTION
              : prId
                ? 'Confirm what arrived, enter what was served, and record the temperatures.'
                : 'No production record has been generated for this mealtime yet. The deliveries below can still be confirmed.'}
        </p>

        {/* One upload per paper (PROD-1511), in one strip where the single scan
            card was: it fills every grade group's table, so it sits with the
            step's header (24px), and the grade groups start 32px below it. */}
        {showScan && (hasTrucks || prId !== null) ? (
          <div className="mt-6">
            <SheetUploadStrip
              key={step.key}
              showDelivery={hasTrucks}
              showRecord={prId !== null}
              deliveryLine={deliveryStripLine(stepDeliveryDates([step]))}
              recordLine={recordStripLine(step.title)}
              onExtract={(photos) => handleScanSheets(photos, [step])}
              disabled={busy}
            />
          </div>
        ) : null}

        {overHeadline ? (
          <div
            role="status"
            data-served-exceeds-received-banner
            className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-[18px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <Info className="mt-px h-4 w-4 shrink-0" aria-hidden />
            <p>
              <span className="font-semibold">{overHeadline}</span> {SERVED_OVER_RECEIVED_NOTE}
            </p>
          </div>
        ) : null}

        {/* The grade groups: each a title, its own table and its claim card, 32px under the step's header. */}
        <div className="mt-8">
          <RecordSpreadsheetTable groups={groups} emptyText="No items on this record." />
        </div>
        {stepCards(step, locked)}

        {/* Two actions and no more: leave, or finish the step (receipts, then the record).
            An amendment has no draft to keep: the header's Cancel leaves, the primary re-signs. */}
        <div className="mt-8 flex flex-col gap-2 md:flex-row md:justify-end">
          {amend ? null : (
            <Button type="button" variant="outline" className="w-full md:w-auto" onClick={onExit} disabled={busy}>
              Save for later
            </Button>
          )}
          {locked && receiptOnly(step) ? (
            <Button
              type="button"
              className="w-full md:w-auto"
              onClick={() => void handleSignAndComplete()}
              disabled={busy || !signGateOk}>
              {busy ? 'Saving…' : 'Confirm deliveries'}
            </Button>
          ) : (prId || hasTrucks) && !locked ? (
            <Button
              type="button"
              className="w-full md:w-auto"
              onClick={() => void handleSignAndComplete()}
              disabled={busy || !signGateOk}>
              {busy ? (prId ? 'Signing…' : 'Saving…') : amend ? 'Sign amendment' : 'Sign & complete'}
            </Button>
          ) : null}
        </div>
      </>
    )
  }

  /**
   * What sits UNDER a step's rows, on every viewport: the unjoined-milk note,
   * the `Write-ins & unservable meals` card, then the Kitchen / Delivery /
   * Service cards. The desktop `renderStep` puts it under the table, the phone
   * wizard under its item cards; one render function so the two never drift.
   */
  function stepCards(step: RecordStep, locked: boolean): ReactNode {
    const prId = step.productionRecordId
    const hasTrucks = step.trucks.length > 0
    const showDelivery = hasTrucks || step.rows.some((row) => cellOffered(row, 'receivedTemp'))
    const signedFacts = prId
      ? (signedLocally[prId] ?? (step.signed ? { name: step.signature.name, signedAt: step.signature.signedAt } : null))
      : null
    return (
      <>
        {prId && step.rows.some((row) => row.milk !== null && !cellOffered(row, 'served')) ? (
          <p className="mt-2 text-xs text-muted-foreground">{UNJOINED_MILK_NOTE}</p>
        ) : null}

        {prId ? (
          <section data-step-card="writeIns" className="mt-8 overflow-hidden rounded-[10px] border bg-card">
            <WriteInsFooter
              variant="card"
              writeIns={edits[prId]?.writeIns ?? []}
              recentItems={recentItems}
              dayItems={dayItemOptions(step.fill?.deliverables ?? [])}
              onAdd={(kind) => addWriteIn(prId, kind)}
              onUpdate={(key, patch) => updateWriteIn(prId, key, patch)}
              onRemove={(key) => removeWriteIn(prId, key)}
            />
          </section>
        ) : null}

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <KitchenCard signatureName={step.kitchen.signatureName} signedAt={step.kitchen.signedAt} />
          {showDelivery ? (
            <DeliveryCard
              hasTrucks={hasTrucks}
              receiverName={receiverNameFor(step)}
              onReceiverNameChange={(next) => patchStep(step.key, (pr) => ({ ...pr, receiverName: next }))}
              time={deliveryTimeFor(step)}
              onTimeChange={(next) => patchStep(step.key, (pr) => ({ ...pr, deliveryReadingTime: next }))}
              deliveryDates={step.trucks.map((truck) => truck.deliveryDate)}
              disabled={busy || (locked && !receiptOnly(step))}
            />
          ) : null}
          {prId ? (
            <ServiceCard
              signature={signatoryName}
              onSignatureChange={(next) => {
                signatureTouchedRef.current = true
                setSignatoryName(next)
              }}
              time={serviceTimeFor(step)}
              onTimeChange={(next) => setServiceTime(step, next)}
              // Amending re-signs: the card stays open and asks why.
              signed={amend ? null : signedFacts}
              disabled={busy}
              amendment={amend ? { reason: amendReason, onReasonChange: setAmendReason } : null}
            />
          ) : null}
        </div>
      </>
    )
  }
}

/**
 * A re-read supersedes the previous one for every cell it FILLED, so a number
 * the model read confidently the second time loses its amber ring; a cell the
 * new photos never reached keeps whatever the last scan said about it.
 */
function nextScanFlags(
  prev: ReadonlySet<string>,
  result: ScanSheetResponse,
  index: ReadonlyMap<string, RecordScanTarget>,
  flags: ReadonlyArray<{ rowKey: string; field: string }>,
): Set<string> {
  const next = new Set(prev)
  for (const scanned of result.rows) {
    const target = index.get(scanned.rowId)
    if (!target) {
      continue
    }
    const { stepKey, row } = target
    for (const [field, cell] of Object.entries(scanned.cells)) {
      if (cell.value === null) {
        continue
      }
      switch (field) {
        // `receivedChecked`: an Orders sheet's tick or empty box speaks to Received too (PROD-1511).
        case 'receivedQuantity':
        case 'receivedChecked':
          next.delete(countCellIdFor(stepKey, row, 'received'))
          break
        case 'dropOffTemperatureF':
          next.delete(tempCellIdFor(row, 'receivedTemp'))
          break
        case 'servingTemperatureF':
          next.delete(tempCellIdFor(row, 'servingTemp'))
          break
        default:
          if (isServiceCountColumn(field as RecordColumn) && row.service?.gradeRow) {
            next.delete(countCellId(stepKey, row.key, field as ServiceCountColumn))
          }
      }
    }
  }
  for (const flag of flags) {
    next.add(`${flag.rowKey}::${flag.field}`)
  }
  return next
}
